import { useEffect, useRef } from 'react';
import Player from 'react-player/file';
import cm from 'classnames';

import { ConfigFlags, PanZoomRotateConfig, PanZoomRotateParams } from 'services/clip.service';
import { useAppSelector } from 'store/store-hooks';
import { useResizeObserver } from 'hooks/use-resize-observer';
import { useAnimation } from 'components/_hooks/use-animation';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { Clip, ClipsMap, LinkedClips, RotateListener } from 'components/app/editor/_mediator/editor.slice';
import { PAN_ZOOM_BE } from 'components/app/editor/pan-zoom/_pan-zoom.params';
import {
  convertPosition,
  getARViewerSize,
  getViewerSize,
  rotatePos,
} from 'components/app/editor/pan-zoom/_mediator/pan-zoom.helper';
import { SB_TRANSITION_PZ } from 'components/app/storyboard/_mediator/sb-const';
import { SbPlaybackParams } from 'components/app/storyboard/_mediator/sb.slice';
import { useSbPlayerContext } from 'components/app/storyboard/sb-player/_mediator/sb-player.provider';
import { SbPanZoomListener, useSbPanZoomContext } from './sb-pan-zoom.provider';
import { isLinked } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';

import styles from './_sb-pan-zoom.module.scss';
import { selectClipData } from 'components/app/_helpers/_app.helper';

const CSS__LEFT = '--frame-left';
const CSS__WIDTH = '--frame-width';
const CSS__HEIGHT = '--frame-height';
const CSS__BOTTOM = '--frame-bottom';
const CSS__DELAY = '--transition-delay';
const CSS__DURATION = '--transition-duration';

export interface Props {
  playerIndex: Index;
  linkedGroupIndex: Index | null;
  clipData: Clip | null;
  rotateData: RotateListener;
  mediaEl: React.MutableRefObject<Player | HTMLImageElement | null>;
  isDamping: boolean;
  isActive: boolean;
  init: boolean;
}

type FramePosition = { width: number; height: number; left: number; bottom: number };
type TransitionParams = { duration: Seconds; delay: Seconds };
type FrameParams = {
  clipId: ClipId;
  start: FramePosition;
  end: FramePosition;
  transition: TransitionParams;
};

export const SbPanZoom = ({
  init,
  clipData,
  rotateData,
  linkedGroupIndex,
  playerIndex,
  isDamping,
  isActive,
  mediaEl,
}: Props) => {
  const { ws } = useAudioTrackContext();
  const { sbPlayers, playerRootElement } = useSbPlayerContext();
  const { sbPanZoomListener, sbPanZoomListenerRef } = useSbPanZoomContext();
  const { runAnimation, stopAnimation } = useAnimation(playerRootElement);
  const { isDragging } = useDraggerContext();
  const clipsMap = useAppSelector((store) => store.editor.clipsMap);
  const linkedClips = useAppSelector((store) => store.editor.linkedClips);
  const transitionTime = useAppSelector((store) => store.editor.storyConfig?.transitionTime);
  const rotateListener = useAppSelector((store) => store.editor.panZoomRotateListener);
  const sbClipPlaybackMap = useAppSelector((store) => store.sb.sbClipPlaybackMap);
  const panZoomControl = useAppSelector((store) => store.sb.panZoomControl);
  const markers = useAppSelector((store) => store.editor.markers);
  const cells = useAppSelector((store) => store.editor.cells);
  const playbackParams = clipData && sbClipPlaybackMap[clipData.clipId];
  const isVisible = isActive || isDamping;
  const rootEl = useRef<HTMLDivElement>(null);
  const viewerEl = useRef<HTMLDivElement>(null);
  const frameEl = useRef<HTMLDivElement>(null);
  const frameParams = useRef<FrameParams | null>(null);

  const handleListener = (listener: SbPanZoomListener) => {
    if (!isActive && isDamping) return { ...listener, cell: listener.cell - 1 };
    return listener;
  };

  const setFrame = (payload: FrameParams) => {
    setFrameStart(frameEl.current!, payload.start);
    setFrameEnd(rootEl.current!, payload.end);
    setFrameTransition(rootEl.current!, payload.transition);
  };

  const initPanZoom = () => {
    if (!init || !playbackParams) return;

    /* setViewer */
    const viewer = getViewerSize(mediaEl.current!, rotateData.rotate);
    if (!viewer) return;
    setViewer(viewerEl.current!, viewer);

    /* setFrame */
    const cb = getPosition(viewerEl.current!, rotateData);
    const config = getConfig(clipData, playbackParams, linkedClips, clipsMap!);
    frameParams.current = {
      clipId: clipData!.clipId,
      start: cb(config[0]),
      end: cb(config[1]),
      transition: getTransition(
        playbackParams,
        sbPlayers.current[playerIndex]!.getDuration(),
        cells!.duration[playbackParams.cellIndex],
        transitionTime ?? 0
      ),
    };
    if (isVisible) {
      if (isDragging.current) return;
      if (ws.current?.isPlaying() && linkedGroupIndex) {
        /* regionIn event, linked clips only, linkedIndex !== 0 */
        setFrame(getOnSeekParams(handleListener(sbPanZoomListenerRef.current), frameParams.current, markers!));
        stopAnimation([SB_TRANSITION_PZ]);
        runAnimation([SB_TRANSITION_PZ]);
      } else setFrame(getOnSeekParams(handleListener(sbPanZoomListener), frameParams.current, markers!));
    } else setFrame(frameParams.current);
  };

  const resetFrameCss = () => {
    frameParams.current && setFrame(frameParams.current);
  };

  /* listeners */

  useResizeObserver(rootEl, initPanZoom);

  useEffect(() => {
    initPanZoom();
  }, [init, clipData?.config.panZoomRotate, playbackParams]);

  useEffect(() => {
    if (isVisible) initPanZoom();
  }, [rotateListener]);

  useEffect(() => {
    if (!init || !playbackParams || !frameParams.current) return;
    if (isVisible) setFrame(getOnSeekParams(handleListener(sbPanZoomListener), frameParams.current, markers!));
  }, [sbPanZoomListener]);

  useEffect(() => {
    if (!init || !playbackParams || isVisible) return;
    resetFrameCss();
  }, [isVisible]);

  return (
    <div
      ref={rootEl}
      className={cm(styles.SbPanZoom, {
        [styles['SbPanZoom--enabled']]: clipData && !panZoomControl,
        [styles['SbPanZoom--active']]: isVisible,
      })}
    >
      <div ref={viewerEl} className={styles.SbPanZoom__viewer}>
        <div ref={frameEl} className={styles.SbPanZoom__frame} />
      </div>
    </div>
  );
};

/* H E L P E R S */

const getPosition = (viewerEl: HTMLDivElement, rotateData: RotateListener) => {
  const viewerRect = viewerEl.getBoundingClientRect();
  return (config: PanZoomRotateParams | null) => {
    const { moveX, moveY, zoom, aspectRatio } = config ?? PAN_ZOOM_BE;
    const { width, height } = getARViewerSize(viewerEl, aspectRatio, rotateData.rotate);
    const position = rotatePos(moveX, moveY, rotateData);
    const left = convertPosition(position.moveX, 'external', viewerRect.width, width * zoom);
    const bottom = convertPosition(position.moveY, 'external', viewerRect.height, height * zoom);
    return { width: width * zoom, height: height * zoom, left, bottom };
  };
};

const getTransition = (
  payload: SbPlaybackParams,
  videoDur: Seconds,
  cellDur: Seconds,
  transitionTime: Milliseconds
) => {
  const { idleStart, endTime, linkedIndex } = payload;
  const transition = linkedIndex && !linkedIndex.isLastInGroup ? 0 : transitionTime / 1000;
  const isImg = endTime === 0;
  const diff = endTime + transition - videoDur;
  const idleEnd = isImg || diff < 0 ? 0 : diff;
  return {
    duration: cellDur + transition - idleStart - idleEnd,
    delay: idleStart,
  };
};

const getFramePosition = (progress: Fraction, frameParams: FrameParams) => {
  const start = frameParams.start;
  const end = frameParams.end;
  return {
    width: start.width + (end.width - start.width) * progress,
    height: start.height + (end.height - start.height) * progress,
    left: start.left + (end.left - start.left) * progress,
    bottom: start.bottom + (end.bottom - start.bottom) * progress,
  };
};

const getOnSeekParams = (payload: SbPanZoomListener, frameParams: FrameParams, markers: MarkerId[]): FrameParams => {
  const { time, cell } = payload;
  const markerTime = cell === 0 ? 0 : markers![cell - 1];
  const cellTime = time - markerTime;
  const { clipId, start, end, transition } = frameParams;
  const { delay, duration } = transition;
  const _delay = cellTime < delay ? delay - cellTime : 0;
  const _duration = _delay ? duration : cellTime > duration + delay ? 0 : duration - (cellTime - delay);
  const position =
    cellTime <= delay
      ? start
      : cellTime >= duration + delay
      ? end
      : getFramePosition((cellTime - delay) / duration, frameParams);
  return { clipId, start: position, end, transition: { duration: _duration, delay: _delay } };
};

const getConfig = (
  clipData: Clip,
  playbackParams: SbPlaybackParams,
  linkedClips: LinkedClips,
  clipMap: ClipsMap
): PanZoomRotateConfig => {
  let start = clipData.config.panZoomRotate[0];
  let end = clipData.config.panZoomRotate[1];
  if (!playbackParams.linkedIndex) return [start, end];

  const { index, isLastInGroup, isFirstInGroup } = playbackParams.linkedIndex;
  if (!isLinked(clipData.clipGroupId)) return [start, end];
  const linkedGroup = linkedClips[clipData.clipGroupId];
  if (!linkedGroup) return [start, end];

  if (!start && !isFirstInGroup) {
    const prevClip = selectClipData(linkedGroup[index - 1], clipMap);
    const isPzEnabled = prevClip.config.flags.includes(ConfigFlags.panZoomRotate);
    if (isPzEnabled) start = prevClip.config.panZoomRotate[1];
  }
  if (!end && !isLastInGroup) {
    const nextClip = selectClipData(linkedGroup[index + 1], clipMap);
    const isPzEnabled = nextClip.config.flags.includes(ConfigFlags.panZoomRotate);
    if (isPzEnabled) end = nextClip.config.panZoomRotate[0];
  }
  return [start, end];
};

const setViewer = (elem: HTMLDivElement, payload: { width: number; height: number }) => {
  const css = elem.style;
  css.width = `${payload.width}px`;
  css.height = `${payload.height}px`;
};

const setFrameStart = (elem: HTMLDivElement, payload: FramePosition) => {
  const css = elem.style;
  css.width = `${payload.width}px`;
  css.height = `${payload.height}px`;
  css.bottom = `${payload.bottom}px`;
  css.left = `${payload.left}px`;
};

const setFrameEnd = (elem: HTMLDivElement, payload: FramePosition) => {
  const { left, bottom, width, height } = payload;
  const css = elem.style;
  css.setProperty(CSS__LEFT, `${left}px`);
  css.setProperty(CSS__WIDTH, `${width}px`);
  css.setProperty(CSS__HEIGHT, `${height}px`);
  css.setProperty(CSS__BOTTOM, `${bottom}px`);
};

const setFrameTransition = (elem: HTMLDivElement, payload: TransitionParams) => {
  const { delay, duration } = payload;
  const css = elem.style;
  css.setProperty(CSS__DURATION, `${duration}s`);
  css.setProperty(CSS__DELAY, `${delay}s`);
};
